<template>
  <div>
    <OrganismsLoading v-show="!journal" />
    <JournalShow
      :article-type="articleType"
      :journal="journal"
      :category-journals="resCategoryJournals?.publicJournalAll?.items ?? []"
      :other-journals="resOtherJournals?.publicJournalAll?.items ?? []"
      :common-part-content="journal.journal_common_part?.content_code"
      :meta="meta"
      :headings="headings"
    />
  </div>
</template>

<script setup lang="ts">
import { createNewsJsonLD, useCustomMeta, createBreadcrumbJsonLD } from "~/composables/meta"
import {
  usePublicJournalAllQuery,
  usePublicJournalOneQuery,
  usePublicProductCategoryOneBySlugQuery,
} from "~/gql/urql.generated"

type Props = {
  articleType?: string
}

withDefaults(defineProps<Props>(), {
  articleType: "",
})

const route = useRoute()
const customMeta = useCustomMeta()
const siteId = useSite().getSiteId()

/**
 * 1. 本カテ取得
 * 2. サブカテ取得
 * 3. 本ページのジャーナル取得
 * 4. カテゴリに紐づくジャーナル一覧 + その他ジャーナル一覧
 */

// 1. 本カテゴリー
const { data: resCategory } = await usePublicProductCategoryOneBySlugQuery({
  requestPolicy: "cache-and-network",
  variables: {
    siteId,
    slug: route.params.category as string,
    parentCategoryId: "",
  },
})
const category = resCategory.value?.publicProductCategoryOneBySlug

// 2. サブカテゴリー
const { data: resSubCategory } = await usePublicProductCategoryOneBySlugQuery({
  requestPolicy: "cache-and-network",
  variables: {
    siteId,
    slug: route.params.subcategory as string,
    parentCategoryId: category?.document_id,
  },
})
const subCategory = resSubCategory.value?.publicProductCategoryOneBySlug

// サブカテゴリが存在しない場合かつサブカテゴリが"-"でない場合は404エラーを返す
if (!subCategory && route.params.subcategory !== "-") {
  throw createError({ statusCode: 404, statusMessage: "Page Not Found subCategory", fatal: process.client })
}

// 3. 本ジャーナル記事
const { data: resJournal } = await usePublicJournalOneQuery({
  requestPolicy: "network-only",
  variables: {
    filter: {
      site_id: siteId,
      slug: route.params.journalSlug as string,
      product_category_id: category?.document_id,
      product_sub_category_id: subCategory?.document_id,
      pass_query: route.query.pass_query ? (route.query.pass_query as string) : undefined,
    },
  },
})
const journal = resJournal.value?.publicJournalOne
if (!journal) {
  throw createError({ statusCode: 404, statusMessage: "Page Not Found journalSlug", fatal: process.client })
}

// 4. **非同期** カテゴリに紐づくジャーナル一覧 + その他のジャーナル一覧
const { data: resCategoryJournals } = usePublicJournalAllQuery({
  requestPolicy: "cache-and-network",
  variables: {
    site_id: siteId,
    filter: {
      other_journal_id: journal.document_id,
      product_category_id: journal.product_category_id,
    },
    page: 1,
    limit: 8,
  },
})

const { data: resOtherJournals } = usePublicJournalAllQuery({
  requestPolicy: "cache-and-network",
  variables: {
    site_id: siteId,
    filter: {
      other_journal_id: journal.document_id,
    },
    page: 1,
    limit: 8,
  },
})

/**
 * 以下本ジャーナル取得後の後処理
 */

useHead(
  customMeta.fetch({
    title: journal.title,
    description: journal.description,
    image: journal.ogp || journal.thumbnail_image || "",
    jsonLDInfo: [
      createNewsJsonLD({
        headline: journal.title,
        image: journal.thumbnail_image,
        description: journal.description,
      }),
      createBreadcrumbJsonLD([
        {
          title: "ジャーナル",
          url: "/journals",
        },
        {
          title: `${category?.name ?? ""}の新着記事`,
          url: `/journals/${category?.slug}`,
        },
        {
          title: `${subCategory?.name ?? ""}の新着記事`,
          url: `/journals/${category?.slug}/${subCategory?.slug}`,
        },
        {
          title: journal.title,
          url: route.fullPath,
        },
      ]),
    ],
  }),
)

const meta = {
  title: journal.title,
  description: journal.description,
  image: journal.thumbnail_image || "",
  url: useRuntimeConfig().public.BASE_URL + route.fullPath,
}

/**
 * h2タグを抽出 → htmlタグを除いた見出しを取り出す
 */
const filterHeadings = (content: string | undefined = ""): string[] => {
  const tags = content.match(/<h2.+?<\/h2>/g)
  return tags == null ? [] : tags.map((tag) => tag.replace(/<[^>]+>/g, ""))
}

const addHeaderId = (text: string | undefined = "", pattern: RegExp): string => {
  const matches = []
  let match
  while ((match = pattern.exec(text))) {
    matches.push(match)
  }
  if (matches.length === 0) return text

  let result = ""
  for (let i = 0; i < matches.length; i++) {
    const index = matches[i].index
    const nextIndex = i < matches.length - 1 ? matches[i + 1].index : text.length
    const tmpStr = text.substring(index, nextIndex)
    result += tmpStr.replace(pattern, `<h2 id='heading${i + 1}'$1>`)
  }
  return result
}

const headings = filterHeadings(journal.content)
journal.content = addHeaderId(journal.content, /<h2.+?>/g)
</script>
